/**
 * AnalysisFreshnessBar — when this analysis was produced, whether it still
 * describes the current selection, and whether the holdings behind it moved.
 *
 * "Aggiornato" and "cambiato" are two different facts and are said separately:
 * an issuer can re-publish the same positions (updated, not changed). The
 * fingerprint that answers the second question covers positions and weights
 * only, so a new "as of" date in the file never raises a false alarm.
 */
import { useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, Clock, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/system";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("it-IT", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
}

function movementLabel(etf) {
  const parts = [];
  if (etf.entered) parts.push(`${etf.entered} entrate`);
  if (etf.exited) parts.push(`${etf.exited} uscite`);
  if (etf.reweighted) parts.push(`${etf.reweighted} pesi variati`);
  return parts.length > 0 ? parts.join(" · ") : "composizione cambiata";
}

export function AnalysisFreshnessBar({
  analysisMeta, aligned = true, onRepeat, disabled = false,
}) {
  const [open, setOpen] = useState(false);
  // "Ripeti analisi" asks first in the two cases where re-running has a
  // consequence the user did not spell out: the selection changed (the saved
  // portfolio would change too), or nothing changed at all (the run is pointless).
  const [ask, setAsk] = useState(null);
  if (!analysisMeta) return null;

  const diff = analysisMeta.diff;
  const summary = diff?.summary;
  const changed = (diff?.etfs || []).filter((e) => e.changed);
  const republished = (diff?.etfs || []).filter((e) => e.updated_after && !e.changed);
  const hasDetail = changed.length > 0 || republished.length > 0;
  const portfolioId = analysisMeta.portfolioId;
  const defaultMode = portfolioId ? "overwrite" : "none";

  const handleRepeat = () => {
    if (portfolioId && !aligned) { setAsk("changed"); return; }
    if (summary && changed.length === 0) { setAsk("nochange"); return; }
    onRepeat?.(defaultMode);
  };

  return (
    <section
      data-testid="analysis-freshness-bar"
      className="rounded-card border border-line-soft bg-surface shadow-card px-3 py-2"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span
          data-testid="analysis-date"
          className="inline-flex items-center gap-1.5 text-label text-content-secondary"
        >
          <Clock className="h-3.5 w-3.5" aria-hidden="true" />
          Analisi del {formatDate(analysisMeta.analyzedAt)}
          {analysisMeta.portfolioName
            ? (analysisMeta.restored
                ? ` · ripristinata da "${analysisMeta.portfolioName}"`
                : ` · salvata in "${analysisMeta.portfolioName}"`)
            : ""}
        </span>

        {!aligned && (
          <Badge variant="warning" data-testid="analysis-alignment-badge">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            Non allineata alla selezione attuale
          </Badge>
        )}

        {summary && changed.length > 0 && (
          <Badge variant="warning" data-testid="analysis-staleness-badge">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            {changed.length} ETF con holdings cambiate
          </Badge>
        )}
        {summary && changed.length === 0 && summary.etfs > 0 && (
          <span
            data-testid="analysis-unchanged-badge"
            className="inline-flex items-center gap-1.5 text-label text-content-muted"
          >
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
            {summary.updated > 0
              ? `Holdings invariate (${summary.updated} ETF ri-pubblicati senza modifiche)`
              : "Holdings invariate dall'analisi"}
          </span>
        )}

        <div className="ml-auto flex items-center gap-1">
          {hasDetail && (
            <button
              type="button"
              data-testid="analysis-freshness-toggle"
              aria-expanded={open}
              aria-controls="analysis-freshness-details"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1 min-h-10 px-2 rounded-md text-label
                         text-content-secondary hover:text-content hover:bg-surface-subtle
                         transition-colors duration-fast ease-system
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            >
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-fast ease-system
                            ${open ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
              {open ? "Nascondi dettaglio" : "Cosa è cambiato"}
            </button>
          )}
          <Button
            data-testid="analysis-repeat-btn"
            size="sm"
            variant={changed.length > 0 || !aligned ? "default" : "outline"}
            disabled={disabled}
            onClick={handleRepeat}
            className={`h-10 px-3 text-xs transition-colors duration-fast ease-system ${
              changed.length > 0 || !aligned
                ? "bg-brand hover:bg-brand-hover text-content-on-brand"
                : ""
            }`}
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
            Ripeti analisi
          </Button>
        </div>
      </div>

      {open && hasDetail && (
        <div
          id="analysis-freshness-details"
          data-testid="analysis-freshness-details"
          className="mt-2 space-y-1 border-t border-line-soft pt-2"
        >
          {changed.map((etf) => (
            <div
              key={etf.isin}
              data-testid={`analysis-changed-${etf.isin}`}
              className="flex flex-wrap items-baseline justify-between gap-2 text-label"
            >
              <span className="min-w-0">
                <span className="font-mono font-semibold !text-brand-accent">
                  {etf.ticker || etf.isin}
                </span>
                <span className="ml-2 text-content-secondary break-words">
                  {etf.etf_name}
                </span>
              </span>
              <span className="text-warning tabular">{movementLabel(etf)}</span>
            </div>
          ))}
          {republished.map((etf) => (
            <div
              key={etf.isin}
              data-testid={`analysis-republished-${etf.isin}`}
              className="flex flex-wrap items-baseline justify-between gap-2 text-label"
            >
              <span className="min-w-0">
                <span className="font-mono font-semibold text-content-secondary">
                  {etf.ticker || etf.isin}
                </span>
                <span className="ml-2 text-content-muted break-words">{etf.etf_name}</span>
              </span>
              <span className="text-content-muted">ri-pubblicato, posizioni identiche</span>
            </div>
          ))}
          <p className="pt-1 text-label text-content-muted">
            Il confronto guarda le posizioni e i pesi delle holdings. Un ETF
            ri-pubblicato senza modifiche non richiede una nuova analisi.
          </p>
        </div>
      )}

      <ConfirmDialog
        open={ask === "nochange"}
        onOpenChange={(v) => !v && setAsk(null)}
        title="Nessuna holding è cambiata"
        description="Dall'ultima analisi le posizioni e i pesi degli ETF selezionati sono identici: rifarla darà lo stesso risultato."
        consequence={portfolioId
          ? `L'analisi salvata in "${analysisMeta.portfolioName}" verrà comunque sostituita da quella nuova.`
          : undefined}
        confirmLabel="Ripeti comunque"
        testId="repeat-analysis-nochange-confirm"
        onConfirm={() => { setAsk(null); onRepeat?.(defaultMode); }}
      />

      <Dialog open={ask === "changed"} onOpenChange={(v) => !v && setAsk(null)}>
        <DialogContent
          className="max-w-[calc(100vw-2rem)] sm:max-w-lg bg-surface border-line text-content"
          data-testid="repeat-analysis-changed-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-base">
              Aggiorno "{analysisMeta.portfolioName}" con la nuova selezione?
            </DialogTitle>
            <DialogDescription className="text-xs text-content-muted">
              Gli ETF o i pesi attuali sono diversi da quelli salvati. Puoi
              aggiornare questo portafoglio, oppure conservarlo così com'è e
              salvare la nuova analisi in un portafoglio nuovo.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              data-testid="repeat-analysis-cancel-btn"
              variant="ghost"
              onClick={() => setAsk(null)}
              className="h-10 text-content-secondary hover:text-content"
            >
              Annulla
            </Button>
            <Button
              data-testid="repeat-analysis-new-btn"
              variant="outline"
              onClick={() => { setAsk(null); onRepeat?.("new"); }}
              className="h-10 border-line"
            >
              Salva come nuovo
            </Button>
            <Button
              data-testid="repeat-analysis-overwrite-btn"
              onClick={() => { setAsk(null); onRepeat?.("overwrite"); }}
              className="h-10 bg-brand hover:bg-brand-hover text-content-on-brand"
            >
              Aggiorna "{analysisMeta.portfolioName}"
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default AnalysisFreshnessBar;
